import { Box, Stack, Alert, Typography } from '@mui/material'
import type { UseQueryResult } from '@tanstack/react-query'
import type { ChartRegistryRow } from '~/api/endpoints/getCharts'
import { usePageControls } from '~/api/hooks/useCharts'
import { labelFor } from './kpiLabel'
import {
  useTimeframeFanout,
  type FanoutTimeframe,
  type WalkSpec,
  type TimeframeFanout,
} from '~/api/hooks/useTimeframeFanout'
import { DimNotApplicableError } from '~/api/types'
import { MetricArmChart } from './MetricArmChart'
import { resolveArmColoring } from '~/components/Charts/resolveArmColoring'
import { PinButton } from '~/components/CollectionPin/PinButton'
import { ChartCardHeader } from '~/components/Charts/chrome/ChartCardHeader'
import { ChartContextCaption } from '~/components/Charts/chrome/ChartContextCaption'
import { ChartLoadingState } from './ChartLoadingState'
import { FilterApplicabilityNote, campaignRatioScopeFor } from './FilterApplicabilityNote'
import type { CampaignRatioScope } from './FilterApplicabilityNote'
import { PendingOverlay } from './PendingOverlay'
import { DimNotApplicableNote } from './DimNotApplicableNote'
import {
  buildChartTitle,
  buildContextCaption,
  timeframePhrase,
  KICKER_BY_CHART_TYPE,
  CHROME,
  MESSAGES,
} from '~/components/Charts/chrome/chartChrome'
import { buildPinV1 } from '~/pin/buildPinV1'
import { useTimeframeLabel } from './useTimeframeLabel'
import {
  useMultiTfTimeframes,
  resolveArmFilters,
  type ResolvedTimeframe,
} from './useMultiTfTimeframes'
import { useDimValueLabelFor } from './useDimValueLabelFor'
import { useSeriesBaseLabels } from './useSeriesBaseLabels'
import { composeChartTitle } from './composeChartTitle'
import type { ReviewPageState } from './useReviewPageState'
import { useDimCatalog } from '~/api/hooks/useCatalog'
import { useDriverCampaignGate } from './useDriverCampaignGate'
import { stripToFactDims } from './stripToFactDims'
import { useColumnsState } from '~/state/useColumnsState'
import { useDimValues } from '~/api/hooks/useDimValues'
import { narrowFanoutToColumn } from './narrowFanoutToColumn'
import { useStore } from '~/state/useStore'
import { ALL_CAMPAIGN_DIMS, CAMPAIGN_EXACT_DIM } from '~/state/campaignPredicate'
import { dimNamesOf } from '~/state/useCampaignState'

interface Props {
  state: ReviewPageState
  charts: UseQueryResult<ChartRegistryRow[], Error>
}

export function MetricDriverBarsTile({ state, charts }: Props) {
  // ── Controls — UNCONDITIONAL, before any early return ──────────────
  const controls = usePageControls(state.factTable)

  // Derive registry row + driver binding (no hook calls here).
  // Direct find by chart_type — findChartRegistryRow excludes metric_driver_bars.
  const row = charts.data?.find((r) => r.chart_type === 'metric_driver_bars')
  const dp = row?.default_params
  const driverKpi = dp?.['driver_kpi'] as string | undefined
  const driverFact = dp?.['driver_fact'] as string | undefined
  // metric_driver_bars rows use walk_ratio_absolute (ratios) or walk_absolute (volumes/nsp).
  const metricFamily: WalkSpec['family'] =
    row?.walk_function === 'walk_ratio_absolute' ? 'ratio_absolute' : 'absolute'
  // C3b Task 5: honesty note for the metric panel — set only when it's a ratio walk AND the
  // active campaign resolved via attribution filters (not the exact Campaign dim). The driver
  // panel is never ratio-family (always walk_absolute), so it never gets this note.
  // I2/M8: the metric arm fires against the page fact, so its applied attribution set is
  // state.campaign.resolvedFilters (already union-pruned) — feed the note that, not the raw predicate.
  const campaignRatioScope = campaignRatioScopeFor(
    metricFamily === 'ratio_absolute',
    state.campaign,
    state.campaign?.resolvedFilters ?? {},
  )

  // Row 16: multi-TF resolves [main, ...picked] (hook call — unconditional). Single mode keeps
  // the tile-specific ISO-range fallback label (pin/title contracts untouched, spec §2).
  const { timeframes: multiTfTimeframes, pending: mtfPending } = useMultiTfTimeframes(state)
  // T3-fold: typed ResolvedTimeframe[] (a superset of FanoutTimeframe, still assignable to every
  // FanoutTimeframe[] consumer below) so the driver-arm loop can read tf.campaignId straight off
  // each element instead of re-deriving it positionally against state.multiTimeframe.entries.
  const timeframes: ResolvedTimeframe[] = multiTfTimeframes ?? [
    {
      id: 'main',
      label: `${state.fromIso}${CHROME.rangeJoiner}${state.toIso}`,
      from: state.fromIso,
      to: state.toIso,
    },
  ]

  // ── Dim catalog — UNCONDITIONAL (for driver-arm dim strip) ────────────────
  const dimCatalog = useDimCatalog()
  // C3 (spec D-C3-1) / M12: the driver fact's OWN controls + the campaign cold-load gate — see
  // useDriverCampaignGate's doc comment for the full rationale (per-fact independent campaign
  // resolution, isPending-not-data-absence gating).
  const { driverControls, driverColdLoad } = useDriverCampaignGate(
    driverFact,
    Boolean(state.campaign),
  )

  // ── Metric fanout — UNCONDITIONAL ───────────────────────────────────
  // Disabled (empty timeframes) when no row.
  // T4b: the main-window spec must use the campaign-merged filter_dims (T2), not the pre-merge
  // state.filters — state.baseParams.filter_dims is already resolved for THIS (page) fact.
  const metricSpec: WalkSpec =
    metricFamily === 'ratio_absolute'
      ? {
          family: 'ratio_absolute',
          kpi: state.kpi,
          filter_dims: state.baseParams.filter_dims,
          group_by: state.groupBy,
        }
      : {
          family: 'absolute',
          kpi: state.kpi,
          fact_table: state.factTable,
          filter_dims: state.baseParams.filter_dims,
          group_by: state.groupBy,
        }
  // FU-02(b): useTimeframeFanout has no `enabled` knob and no single-window mode — hold by passing
  // [] (no walk fires) while the arms' ratio-pair machinery is pending. mtfPending false otherwise.
  const metric = useTimeframeFanout(metricSpec, row && !mtfPending ? timeframes : [])

  // ── Driver fanout — UNCONDITIONAL ───────────────────────────────────
  // Disabled (empty timeframes) when no driver binding.
  // When driverKpi is absent we pass a dummy spec (no walk fires; timeframes = []).
  // Strip the driver arm's filters/group-by to dims the driver fact actually carries,
  // so dims the spend fact can't handle (e.g. KPI dims) don't blank the spend graph.
  // T4b (T3-reviewer minor): spread ALL_CAMPAIGN_DIMS into the loading fallback too — mirrors the
  // metric-side hardening in useMultiTfTimeframes — otherwise the exact Campaign key is
  // transiently silently stripped from the driver arms while the catalog is still loading.
  const driverDims: Set<string> = dimCatalog.data
    ? new Set(dimCatalog.data.filter((r) => r.fact_table === driverFact).map((r) => r.dim_name))
    : new Set([...Object.keys(state.filters), ...state.groupBy, ...ALL_CAMPAIGN_DIMS]) // fall back: pass everything through
  const dvr = driverKpi ? stripToFactDims(state.filters, state.groupBy, driverDims) : null
  const driverFactCarriesCampaign = dimNamesOf(driverControls.data).has(CAMPAIGN_EXACT_DIM)
  // T4b: the driver's OWN main-window filter_dims — campaign-resolved independently for the
  // DRIVER fact (mirrors state.baseParams.filter_dims's page-fact resolution in T2), via the
  // SAME resolveArmFilters rule the per-arm loop below already uses. No campaign active -> keep
  // today's strip-only value, unchanged.
  const dvrFilterDims = driverKpi
    ? state.campaign
      ? resolveArmFilters(state.campaign.item, driverFactCarriesCampaign, state.filters, driverDims)
      : dvr!.filters
    : null
  // Campaigns C3 (spec D-C3-1, supersedes the C1 Task 7 strip-only fix): a per-arm filter_dims
  // resolved in useMultiTfTimeframes is fact-aware for the METRIC fact only — stripping it to the
  // driver's dims isn't enough when the two facts disagree on carrying the exact Campaign dim
  // (model_success_driver may carry it while the consumer fact doesn't, or vice versa). Re-resolve
  // each campaign arm from the SAME campaign_timeslots item (fact-independent, mig 0084) against
  // the DRIVER fact's own carrying flag via the shared resolveArmFilters (the ONE rule).
  // T4b fix-wave precision: arms WITHOUT their own campaign item keep the strip-only mapping of
  // their explicit tf.filter_dims; arms with NO per-arm filter_dims at all inherit the driver
  // spec's dvrFilterDims via useTimeframeFanout's `tf.filter_dims ?? spec.filter_dims` — under an
  // active PAGE campaign that is the campaign-resolved set, BY DESIGN (the metric side inherits
  // the same via baseParams). Likewise a picked WINDOW-ONLY campaign (empty resolved predicate):
  // mergeArmFilters early-returns its base unchanged, so that arm too inherits the page
  // campaign's resolved filters rather than an unfiltered window.
  // T3-fold: match by the arm's OWN campaignId (carried on the resolved timeframe entry itself,
  // set by useMultiTfTimeframes in the same branch as filter_dims) instead of positionally
  // cross-referencing state.multiTimeframe.entries — removes the silent-wrong-campaign hazard if
  // the two arrays ever drift/reorder. tf.campaignId is naturally absent on the main arm (i=0)
  // and on an unresolved/window-only picked arm, matching the old i>0 guard's effect exactly.
  const driverTimeframes: FanoutTimeframe[] = timeframes.map((tf) => {
    const campaignId = tf.campaignId
    const item = campaignId
      ? controls.data?.campaign_timeslots?.items.find((it) => it.value === campaignId)
      : undefined
    if (item) {
      return {
        ...tf,
        filter_dims: resolveArmFilters(
          item,
          driverFactCarriesCampaign,
          dvrFilterDims ?? {},
          driverDims,
        ),
      }
    }
    return tf.filter_dims
      ? { ...tf, filter_dims: stripToFactDims(tf.filter_dims, [], driverDims).filters }
      : tf
  })
  const driver = useTimeframeFanout(
    driverKpi
      ? {
          family: 'absolute',
          kpi: driverKpi,
          fact_table: driverFact!,
          filter_dims: dvrFilterDims!,
          group_by: dvr!.groupBy,
        }
      : {
          family: 'absolute',
          kpi: state.kpi,
          fact_table: state.factTable,
          filter_dims: {},
          group_by: [],
        },
    driverKpi && !driverColdLoad && !mtfPending ? driverTimeframes : [],
  )

  // ── Chrome inputs (rows 4/5/25) — UNCONDITIONAL ───────────────
  const mainTimeframeLabel = useTimeframeLabel(state)
  const valueLabelFor = useDimValueLabelFor(state.factTable)
  // Render-only: maps raw series dim-value keys -> display labels for the bar legends (row 8)
  const seriesBaseLabelFor = useSeriesBaseLabels(state.factTable, state.groupBy)

  // ── Columns fan-out — UNCONDITIONAL (hooks before early returns) ─────────────
  const [columnsDim] = useColumnsState(state.groupBy)
  const dimValues = useDimValues(columnsDim, state.factTable, state.kpi)

  // ── Color catalog (spec D3) — UNCONDITIONAL ───────────────────────────────
  // A single group-by dim's FULL catalog value list feeds resolveArmColoring so bar
  // colors follow catalog/filter-panel order, not first-seen render order. Multiple (or
  // zero) group-by dims have no single-dim catalog to key against — dimComboKey joins
  // multi-dim keys with ' / ' — so those fall back to the overflow-only path (old
  // locale-sorted colors, catalogValues = []). NOTE: when a "columns" fan-out narrows
  // rendered bars to fewer dims than `state.groupBy` (see `fanout` below), this stays
  // conservative — it does not attempt to catalog the post-narrowing remainder — a
  // known, accepted limitation (see task-5-report.md).
  const groupByDim = state.groupBy.length === 1 ? state.groupBy[0]! : ''
  const colorCatalog = useDimValues(groupByDim, state.factTable, state.kpi)
  const palette = useStore((s) => s.personal.chipPalette)

  // ── Early returns (after all hooks) ─────────────────────────────────────────
  if (charts.isLoading) return null
  if (charts.error) return <Alert severity="warning">{MESSAGES.chartLoadFailed}</Alert>
  if (!row) return <Alert severity="warning">{MESSAGES.noChartConfiguredForKpi}</Alert>
  // FU-02(b): explicit loading render — useTimeframeFanout with [] reports no loading, so the hold
  // must render a loading state, never empty arms (mirrors the per-arm ChartLoadingState below).
  if (mtfPending) return <ChartLoadingState />

  // ── Labels from controls.kpi_metadata.items (NOT flat array) ────────────────
  const metricLabel = labelFor(controls, state.kpi)
  const driverLabel = driverKpi ? labelFor(controls, driverKpi) : ''

  // ── Shared color map + small-multiples flag (P6 fix: same dim value = same color across arms) ──
  // fanout drives BOTH the colour-map strip AND the body branch so the two can never disagree.
  // In the transient where columnsDim is set but dimValues is still empty, fanout=false keeps
  // the colour map over the full (unstripped) keyspace, matching the armPair(metric, driver) render.
  const fanout = columnsDim !== '' && dimValues.data.length > 0
  const colorMetric = fanout ? narrowFanoutToColumn(metric, columnsDim) : metric
  const colorDriver = driverKpi
    ? fanout
      ? narrowFanoutToColumn(driver, columnsDim)
      : driver
    : null
  const { colorForKey, smallMultiples } = resolveArmColoring(
    colorMetric.byTimeframe,
    colorDriver?.byTimeframe ?? null,
    timeframes.length,
    groupByDim ? colorCatalog.data : [],
    palette,
  )

  // FU-m: per-arm isolation. Errored arm → typed group-by note or scoped generic note;
  // healthy arm → chart + its OWN per-walk applicability note (driver arm evaluates the
  // driver fact+kpi, closing the V6 "state.kpi only" gap).
  // T4b fix-wave (spec §3.3): each arm's note also evaluates the filters THAT arm's walk fired
  // with — metric: baseParams.filter_dims; driver: the driver-fact-resolved dvrFilterDims.
  const armContent = (
    arm: TimeframeFanout,
    label: string,
    factTable: string,
    kpi: string,
    filters: Record<string, string[]>,
    domain?: [number, number],
    // C3b Task 5: only the metric-arm caller passes this (the driver arm is never ratio-family).
    // M9: named type over the inline ReturnType<typeof campaignRatioScopeFor>.
    armCampaignRatioScope?: CampaignRatioScope | null,
    // FU-33: the cold-load window empties this arm's fanout (settled, isLoading:false) — the
    // caller passes its own pending flag so the hold covers the disabled window too.
    coldLoadPending = false,
  ) => {
    if (arm.isError) {
      const err = arm.byTimeframe.find((s) => s.isError)?.error ?? null
      return err instanceof DimNotApplicableError ? (
        <DimNotApplicableNote dims={err.dims} metricLabel={label} />
      ) : (
        <Alert severity="warning">{MESSAGES.chartDataLoadFailed}</Alert>
      )
    }
    if (arm.isLoading || coldLoadPending) return <ChartLoadingState />
    return (
      <>
        <PendingOverlay active={arm.isPlaceholderData}>
          <MetricArmChart
            label={label}
            slices={arm.byTimeframe}
            colorForKey={colorForKey}
            labelForKey={seriesBaseLabelFor}
            domain={domain}
          />
        </PendingOverlay>
        <FilterApplicabilityNote
          state={state}
          factTable={factTable}
          kpi={kpi}
          filters={filters}
          campaignRatioScope={armCampaignRatioScope}
        />
      </>
    )
  }

  // Shared scale helper: [min, max] domain across all timeframes for an arm (Task 9's tuple
  // contract, adopted here so negative driver/metric rows get axis room instead of clipping at 0).
  // Row-level scan is safe here because walk rows are unique per full group-by combo: after
  // narrowFanoutToColumn filters to one column value and strips that dim, each remaining
  // group-by key appears exactly once per timeframe (no summing occurs in toRankedDimRows).
  // If row-uniqueness ever breaks (e.g. a new aggregation step), revisit this assumption.
  const sharedDomain = (f: TimeframeFanout): [number, number] => {
    let lo = 0
    let hi = 0
    for (const s of f.byTimeframe) {
      for (const r of s.rows) {
        const v = r.value ?? 0
        if (v < lo) lo = v
        if (v > hi) hi = v
      }
    }
    return [lo, hi]
  }

  // Render one metric+driver pair with optional per-arm shared domains.
  const armPair = (
    metricArm: TimeframeFanout,
    driverArm: TimeframeFanout,
    metricDomain?: [number, number],
    driverDomain?: [number, number],
  ) => (
    <Stack direction={smallMultiples ? 'column' : { xs: 'column', md: 'row' }} spacing={2}>
      <Box sx={{ flex: 1 }}>
        {armContent(
          metricArm,
          metricLabel,
          state.factTable,
          state.kpi,
          state.baseParams.filter_dims,
          metricDomain,
          campaignRatioScope,
        )}
      </Box>
      {driverKpi ? (
        <Box sx={{ flex: 1 }}>
          {armContent(
            driverArm,
            driverLabel,
            driverFact!,
            driverKpi,
            dvrFilterDims!,
            driverDomain,
            undefined,
            driverColdLoad,
          )}
        </Box>
      ) : null}
    </Stack>
  )

  // ── Chrome (rows 4/5/25) — render-truthful: the title names the fanout labels when
  // multi-TF is active, else the main window. No unit (the metric/driver pair mixes scales).
  const tfLabels = multiTfTimeframes ? multiTfTimeframes.map((t) => t.label) : [mainTimeframeLabel]
  const cardTitle = buildChartTitle({
    kpiTitle: composeChartTitle(state.kpiCascade),
    timeframePhrase: timeframePhrase(tfLabels),
  })
  // T4b: caption mirrors the metric arm's actual filter_dims, not the pre-merge state.filters.
  const captionLines = buildContextCaption({
    filters: state.baseParams.filter_dims,
    groupBy: state.groupBy,
    valueLabelFor,
  })

  // ── Pin ──────────────────────────────────────────────────────────────────────
  const pin = buildPinV1({
    walk_function:
      row.walk_function === 'walk_ratio_absolute' ? 'walk_ratio_absolute' : 'walk_absolute',
    fact_table: state.factTable,
    kpi: state.kpi,
    // FY v2: freeze the RAW display unit (not governance.effectiveUnit) — replay must
    // reproduce what THIS tile folds at (ChartCanvas reads the raw unit), spec §3.2. No time
    // axis here (metric_driver_bars), but recorded anyway per spec §3.2.
    render_unit: state.granularity.unit,
    params: {
      from: state.fromIso,
      to: state.toIso,
      // T4b: freeze exactly the metric arm's fired filter_dims (baseParams — T2 campaign-merged).
      filter_dims: state.baseParams.filter_dims,
      group_by: state.groupBy,
    },
    chart_type: 'metric_driver_bars',
    viz_params: {
      ...(driverKpi
        ? {
            driver_kpi: driverKpi,
            driver_fact: driverFact,
            driver_value_type: dp?.['driver_value_type'] as string | undefined,
          }
        : {}),
      // Campaigns C1 Task 8: `timeframes` is the SAME array fed to the live metric fanout
      // above (row 16/Task 7) — freezing it here carries each arm's resolved filter_dims
      // (when set) and its already-suffixed "(window only)" label straight into the pin.
      // Do not narrow/remap this array before assigning it — that would silently drop the
      // per-arm predicate a saved pin needs for replay (ChartFromPin's `tf.filter_dims ?? base`).
      // T3-fold: the ONE exception is campaignId — a live, same-session lookup aid (see
      // ResolvedTimeframe's doc comment), never part of the wire shape a saved pin replays
      // (id/label/from/to/filter_dims only) — strip it, everything else passes through verbatim.
      timeframes: timeframes.map(({ campaignId: _campaignId, ...tf }) => tf),
    },
  })

  return (
    <Stack spacing={0.5}>
      <ChartCardHeader
        kicker={KICKER_BY_CHART_TYPE['metric_driver_bars']!}
        title={cardTitle}
        actions={<PinButton pin={pin} />}
      />
      {fanout ? (
        <Stack spacing={2}>
          {(() => {
            const metricDomain = sharedDomain(narrowFanoutToColumn(metric, columnsDim))
            const driverDomain = driverKpi
              ? sharedDomain(narrowFanoutToColumn(driver, columnsDim))
              : undefined
            return dimValues.data.map((v) => (
              <Box key={v}>
                <Typography variant="subtitle1">
                  {columnsDim}: {seriesBaseLabelFor(v)}
                </Typography>
                {armPair(
                  narrowFanoutToColumn(metric, columnsDim, v),
                  narrowFanoutToColumn(driver, columnsDim, v),
                  metricDomain,
                  driverDomain,
                )}
              </Box>
            ))
          })()}
        </Stack>
      ) : (
        armPair(metric, driver)
      )}
      <ChartContextCaption lines={captionLines} />
    </Stack>
  )
}
